import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Trophy, ArrowRight, RefreshCw, AlertCircle, Code2, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/Logo";
import { PboLiveBoard } from "@/components/PboLiveBoard";
import useContestSocket from "@/hooks/useContestSocket";
import { fetchPublicStandings, refreshContestScores } from "@/lib/contestApi";
import { toast } from "sonner";

const fmtMoney = (n) =>
  typeof n === "number" ? `$${n.toLocaleString()}` : "$0";

const RelTime = ({ iso }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  if (!iso) return null;
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Math.floor((now - t) / 1000));
  if (diff < 30) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const StandingsList = ({ standings, data, isOpen, toggleRow, barPct, leaderPts }) => (
  <div className="space-y-2" data-testid="public-standings-list">
    {standings.map((row) => {
      if (row.picks_hidden) {
        return (
          <div
            key={row.participant_id}
            className="border border-pbo-border rounded-xl bg-white px-4 py-3 flex items-center justify-between gap-3"
            data-testid={`public-standings-row-${row.participant_id}`}
          >
            <div className="min-w-0">
              <div className="font-serif text-pbo-green text-lg leading-tight truncate">{row.team_name || row.username}</div>
              {row.team_name && <div className="text-[11px] text-pbo-muted">{row.username}</div>}
            </div>
            <span
              className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full shrink-0 ${
                row.has_submitted ? "bg-pbo-green/10 text-pbo-green-soft" : "bg-pbo-paper text-pbo-muted"
              }`}
              data-testid={`picks-hidden-${row.participant_id}`}
            >
              {row.has_submitted ? "Lineup in ✓" : "No lineup yet"}
            </span>
          </div>
        );
      }
      const hasAnyScore = row.picks.some((p) => p.points != null);
      const roundTotals = row.round_totals || [];
      const behind = hasAnyScore ? Math.round((leaderPts - row.total_points) * 100) / 100 : null;
      const open = isOpen(row.participant_id);
      return (
        <div
          key={row.participant_id}
          className="border border-pbo-border rounded-xl overflow-hidden bg-white"
          data-testid={`public-standings-row-${row.participant_id}`}
        >
          <button
            type="button"
            onClick={() => toggleRow(row.participant_id)}
            className="w-full text-left hover:bg-pbo-paper/60"
            aria-expanded={open}
          >
            <div className="flex items-center justify-between gap-3 px-4 pt-4 pb-2">
              <div className="flex items-center gap-4 min-w-0">
                <span className="w-10 flex flex-col items-center shrink-0">
                  <span
                    className={`font-mono font-bold text-2xl leading-tight ${
                      row.rank === 1 ? "text-pbo-gold" : row.rank === 2 ? "text-pbo-green-soft" : "text-pbo-muted"
                    }`}
                  >
                    #{row.rank}
                  </span>
                  {row.movement > 0 && (
                    <span className="text-[10px] font-bold text-emerald-600 leading-none">▲{row.movement}</span>
                  )}
                  {row.movement < 0 && (
                    <span className="text-[10px] font-bold text-red-600 leading-none">▼{-row.movement}</span>
                  )}
                </span>
                <div className="min-w-0">
                  <div className="font-serif text-pbo-green text-xl leading-tight truncate">
                    {row.team_name || row.username}
                  </div>
                  <div className="text-[11px] text-pbo-muted">
                    {row.team_name ? `${row.username} · ` : ""}
                    {row.picks.length} picks · {fmtMoney(row.total_salary)}
                  </div>
                  {hasAnyScore && roundTotals.length > 0 && (
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                      {roundTotals.map((rt, i) => (
                        <span
                          key={i}
                          className={`text-[11px] font-mono px-2 py-0.5 rounded font-semibold ${
                            rt > 0 ? "bg-pbo-green/10 text-pbo-green-soft" : rt < 0 ? "bg-red-50 text-red-700" : "bg-pbo-paper text-pbo-muted"
                          }`}
                        >
                          {(data.round_labels || [])[i] || `R${i + 1}`} {rt > 0 ? `+${rt}` : rt}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div
                  className={`font-serif text-3xl leading-none ${
                    row.total_points > 0 ? "text-pbo-green" : row.total_points < 0 ? "text-red-600" : "text-pbo-ink"
                  }`}
                >
                  {hasAnyScore ? row.total_points : "—"}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-pbo-muted font-semibold mt-1">
                  {behind == null ? "pts" : behind === 0 ? (row.rank === 1 ? "Leader" : "Tied lead") : `−${behind} back`}
                </div>
              </div>
            </div>
            {hasAnyScore && (
              <div className="px-4 pb-3" data-testid={`gap-bar-${row.participant_id}`}>
                <div className="h-2 rounded-full bg-pbo-paper overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-[width] duration-700 ${
                      row.rank === 1 ? "bg-pbo-gold" : "bg-pbo-green/70"
                    }`}
                    style={{ width: `${barPct(row.total_points)}%` }}
                  />
                </div>
              </div>
            )}
          </button>
          {open && (
            <div className="border-t border-pbo-border/60 bg-pbo-paper/40">
              <div className="px-4 pt-3 pb-1 grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-3 items-baseline text-[10px] uppercase tracking-[0.15em] font-semibold text-pbo-muted">
                <span>Golfer</span>
                <span className="text-right w-14">Salary</span>
                <span className="flex gap-2">
                  {(row.picks[0]?.round_points || []).map((_, i) => (
                    <span key={i} className="w-9 text-center">
                      {(data.round_labels || [])[i] || `R${i + 1}`}
                    </span>
                  ))}
                </span>
                <span className="text-right w-12">Total</span>
              </div>
              <ul className="px-4 pb-3">
                {row.picks.map((p) => (
                  <li
                    key={p.golfer_id}
                    className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-3 items-center text-sm py-1"
                  >
                    <span className="text-pbo-ink truncate">
                      {p.name}
                      {p.bonus > 0 && (
                        <span className="ml-1.5 text-[10px] font-mono font-semibold bg-pbo-gold/20 text-pbo-ink rounded px-1" title="Finish bonus">
                          +{p.bonus}
                        </span>
                      )}
                    </span>
                    <span className="font-mono text-xs text-pbo-muted text-right w-14">{fmtMoney(p.salary)}</span>
                    <span className="flex gap-2">
                      {(p.round_points || []).map((rp, i) => (
                        <span
                          key={i}
                          className={`font-mono text-xs w-9 text-center tabular-nums ${
                            rp == null ? "text-pbo-muted/60" : rp > 0 ? "text-pbo-green-soft" : rp < 0 ? "text-red-600" : "text-pbo-ink"
                          }`}
                          title={`Round ${i + 1}`}
                        >
                          {rp == null ? "—" : rp}
                        </span>
                      ))}
                    </span>
                    <span
                      className={`font-mono font-semibold w-12 text-right tabular-nums ${
                        p.points == null ? "text-pbo-muted" : p.points > 0 ? "text-pbo-green-soft" : p.points < 0 ? "text-red-600" : "text-pbo-ink"
                      }`}
                    >
                      {p.points == null ? "—" : p.points}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    })}
  </div>
);

export default function ContestStandings() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const upperCode = (code || "").toUpperCase();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openIds, setOpenIds] = useState(() => new Set());
  const [expandedAll, setExpandedAll] = useState(params.get("expanded") === "1");
  const toggleRow = (id) =>
    setOpenIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const isOpen = (id) => expandedAll || openIds.has(id);
  const customTitle = (params.get("title") || "").trim();
  const embedTitle = customTitle || (data ? data.name : upperCode);
  const embedSnippet = `<iframe src="${window.location.origin}/contest/${upperCode}/standings?embed=1&expanded=1&title=${encodeURIComponent(`PBO ${new Date().getFullYear()} Fantasy Standings`)}" title="PBO ${new Date().getFullYear()} Fantasy Standings" style="width:100%;min-height:720px;border:0;border-radius:16px" loading="lazy"></iframe>`;

  const load = useCallback(async () => {
    try {
      const d = await fetchPublicStandings(upperCode);
      setData(d);
    } catch {
      toast.error("Contest not found");
    } finally {
      setLoading(false);
    }
  }, [upperCode]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh every 30s when locked
  useEffect(() => {
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  // Instant push when the backend re-scores (PBO stream)
  useContestSocket(upperCode, () => load());

  const onRefresh = async () => {
    if (!data?.has_scoring_url) {
      toast.error("Host hasn't configured a scoring source yet");
      return;
    }
    setRefreshing(true);
    try {
      const res = await refreshContestScores(upperCode);
      if (res.ok === false) {
        toast.error(`Score fetch error: ${res.error || "unknown"}`);
      } else {
        toast.success("Scores refreshed");
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-pbo-cream">
        <SiteHeader />
        <div className="text-center p-10 text-pbo-muted">Loading standings…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-pbo-cream">
        <SiteHeader />
        <div className="max-w-xl mx-auto p-10 text-center">
          <h1 className="font-serif text-pbo-green text-3xl">Contest not found</h1>
          <Link to="/" className="text-pbo-gold mt-4 inline-block hover:underline">
            ← Back to landing
          </Link>
        </div>
      </div>
    );
  }

  const standings = data.standings || [];
  const scored = standings.filter((r) => r.picks.some((p) => p.points != null));
  const leaderPts = scored.length ? Math.max(...scored.map((r) => r.total_points)) : 0;
  const floorPts = scored.length ? Math.min(0, ...scored.map((r) => r.total_points)) : 0;
  const barPct = (pts) => {
    const span = leaderPts - floorPts;
    if (!span || span <= 0) return pts === leaderPts ? 100 : 6;
    return Math.max(6, Math.round(((pts - floorPts) / span) * 100));
  };

  if (embed) {
    return (
      <div className="bg-pbo-cream text-pbo-ink p-3 sm:p-4" data-testid="standings-embed">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-pbo-muted font-semibold flex items-center gap-2">
              <Trophy className="w-3 h-3 text-pbo-gold" /> {customTitle ? "Palm Breezy Open" : "Fantasy standings"}
              {data.status === "final" && <span className="px-1.5 py-0.5 rounded bg-pbo-gold text-pbo-ink font-bold">Final</span>}
              {data.status === "locked" && <span className="inline-flex items-center gap-1 text-pbo-green"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />Live</span>}
              {data.status === "open" && <span className="text-pbo-muted normal-case tracking-normal">· lineups hidden until lock</span>}
            </div>
            <div className="font-serif text-pbo-green text-2xl leading-tight truncate" data-testid="embed-title">{embedTitle}</div>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-pbo-muted">
            {data.scores_updated_at && data.status !== "final" && (
              <span data-testid="standings-updated-at">Updated <RelTime iso={data.scores_updated_at} /></span>
            )}
            {standings.length > 0 && data.status !== "open" && (
              <button
                type="button"
                onClick={() => { setExpandedAll((v) => !v); setOpenIds(new Set()); }}
                className="inline-flex items-center gap-1 font-semibold text-pbo-green hover:text-pbo-gold"
                data-testid="standings-expand-all-btn"
              >
                {expandedAll ? <ChevronsDownUp className="w-3.5 h-3.5" /> : <ChevronsUpDown className="w-3.5 h-3.5" />}
                {expandedAll ? "Collapse" : "Full breakdown"}
              </button>
            )}
          </div>
        </div>
        {standings.length === 0 ? (
          <div className="bg-white border border-dashed border-pbo-border rounded-2xl text-center py-10 text-pbo-muted text-sm">
            {data.status === "open" ? "Teams appear here as they join — lineups stay hidden until lock." : "No teams submitted before lock."}
          </div>
        ) : (
          <StandingsList standings={standings} data={data} isOpen={isOpen} toggleRow={toggleRow} barPct={barPct} leaderPts={leaderPts} />
        )}
        <div className="mt-3 text-right text-[10px] text-pbo-muted">
          <a href={`${window.location.origin}/contest/${upperCode}/standings`} target="_blank" rel="noreferrer" className="hover:text-pbo-green">
            Powered by PBO Fantasy Draft ↗
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pbo-cream">
      <SiteHeader
        rightSlot={
          <Link
            to={`/contest/${upperCode}`}
            className="text-sm font-semibold text-pbo-green hover:text-pbo-gold"
            data-testid="enter-contest-link"
          >
            Enter contest <ArrowRight className="w-3.5 h-3.5 inline" />
          </Link>
        }
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="rounded-3xl bg-pbo-green text-pbo-cream p-7 mb-6 relative overflow-hidden">
          <div className="absolute inset-0 pbo-stripe opacity-20 pointer-events-none" />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.3em] text-pbo-gold font-semibold flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" />
              Public standings
              {data.status === "locked" && (
                <span className="px-2 py-0.5 rounded-full bg-pbo-gold/30 text-pbo-cream">
                  Locked
                </span>
              )}
              {data.status === "final" && (
                <span className="px-2 py-0.5 rounded-full bg-pbo-gold text-pbo-ink font-bold">
                  Final
                </span>
              )}
              {data.status === "open" && (
                <span className="px-2 py-0.5 rounded-full bg-pbo-cream/15 text-pbo-cream">
                  Picks open
                </span>
              )}
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl mt-1">{data.name}</h1>
            <div className="flex items-baseline gap-4 mt-3 flex-wrap text-sm text-pbo-cream/80">
              <span>
                Code{" "}
                <strong className="font-mono text-pbo-gold tracking-widest text-lg">
                  {data.code}
                </strong>
              </span>
              <span>·</span>
              <span>
                Cap <strong>{fmtMoney(data.salary_cap)}</strong>
              </span>
              <span>·</span>
              <span>
                Roster <strong>{data.roster_size}</strong>
              </span>
              <span>·</span>
              <span>
                {data.entries_count} team{data.entries_count === 1 ? "" : "s"}
              </span>
            </div>
            {data.scores_updated_at && data.status !== "final" && (
              <p className="text-xs text-pbo-cream/60 mt-3" data-testid="standings-updated-at">
                Scores updated <RelTime iso={data.scores_updated_at} /> · auto-refreshes
              </p>
            )}
            {data.status === "final" && (
              <p className="text-xs text-pbo-gold mt-3" data-testid="public-final-note">
                <Trophy className="w-3 h-3 inline mr-1" />
                Final results
                {data.finalized_at
                  ? ` · ${new Date(data.finalized_at).toLocaleDateString()}`
                  : ""}{" "}
                — congratulations to {(() => { const w = standings.find((r) => r.rank === 1); return w ? w.team_name || w.username : "the champion"; })()}!
              </p>
            )}
            {data.payout_preview && (
              <div
                className="mt-3 flex items-center gap-2 flex-wrap text-xs"
                data-testid="public-payout-preview"
              >
                <span className="font-semibold text-pbo-gold">
                  Pot ${Number(data.payout_preview.pot).toLocaleString()}
                </span>
                {data.payout_preview.payouts.map((p, i) => (
                  <span
                    key={i}
                    className="bg-pbo-cream/10 rounded-full px-2.5 py-1 text-pbo-cream/90"
                  >
                    {["1st", "2nd", "3rd", "4th", "5th"][i] || `${i + 1}th`} $
                    {Number(p).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Refresh + warnings */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <div className="text-sm text-pbo-ink/70">
            {data.status === "open"
              ? "Lineups are private until lock — teams are listed, picks and points appear the moment picks lock."
              : data.entries_count === 0
              ? "No teams submitted before lock."
              : `Ranked by total points.`}
          </div>
          {data.status === "locked" && data.has_scoring_url && (
            <Button
              onClick={onRefresh}
              disabled={refreshing}
              variant="outline"
              className="rounded-full h-9 text-xs border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream"
              data-testid="standings-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing" : "Refresh scores"}
            </Button>
          )}
          {standings.length > 0 && data.status !== "open" && (
            <button
              type="button"
              onClick={() => { setExpandedAll((v) => !v); setOpenIds(new Set()); }}
              className="inline-flex items-center gap-1 text-xs font-semibold text-pbo-green hover:text-pbo-gold"
              data-testid="standings-expand-all-btn"
            >
              {expandedAll ? <ChevronsDownUp className="w-3.5 h-3.5" /> : <ChevronsUpDown className="w-3.5 h-3.5" />}
              {expandedAll ? "Collapse all" : "Full breakdown"}
            </button>
          )}
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(embedSnippet);
                toast.success("Embed code copied — paste it into your website");
              } catch {
                toast.error("Couldn't copy — long-press the URL bar instead");
              }
            }}
            className="inline-flex items-center gap-1 text-xs font-semibold text-pbo-muted hover:text-pbo-green"
            data-testid="standings-embed-code-btn"
          >
            <Code2 className="w-3.5 h-3.5" /> Embed on your site
          </button>
        </div>

        {data.scores_error && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>Last fetch error: {data.scores_error}</span>
          </div>
        )}

        {/* Standings list */}
        {standings.length === 0 ? (
          <div className="bg-white border border-dashed border-pbo-border rounded-2xl text-center py-14 text-pbo-muted text-sm">
            {data.status === "open"
              ? "Teams appear here as they join — lineups stay hidden until lock."
              : "No teams submitted before lock."}
          </div>
        ) : (
          <StandingsList standings={standings} data={data} isOpen={isOpen} toggleRow={toggleRow} barPct={barPct} leaderPts={leaderPts} />
        )}

        {data.pbo_live_url && (
          <div className="mt-8">
            <PboLiveBoard url={data.pbo_live_url} tournamentName={data.pbo_tournament_name} />
          </div>
        )}

        <div className="mt-8 text-center text-xs text-pbo-muted">
          Bookmark this page — anyone with the link can check the live standings.
        </div>
      </div>
    </div>
  );
}
